import {
  sharedActivityFrequencyStep,
  sharedSleepQualityStep,
  sharedStressLevelStep,
} from '@/flow/shared/commonQuestions'
import { validateTextoLivre } from '@/flow/validators'
import type { FlowContext, Step } from '@/types'

const hasConcern = (concern: string) => (ctx: FlowContext) => {
  const concerns = ctx.vars.preocupacoes_sono
  return Array.isArray(concerns) && concerns.includes(concern)
}

const hasAnyConcern =
  (...concerns: string[]) =>
  (ctx: FlowContext) => {
    const selectedConcerns = ctx.vars.preocupacoes_sono
    return Array.isArray(selectedConcerns) && concerns.some((concern) => selectedConcerns.includes(concern))
  }

const hasCurrentTreatment = (ctx: FlowContext) => {
  const treatments = ctx.vars.tratamentos_sono
  return Array.isArray(treatments) && treatments.length > 0
}

const hasTreatment = (treatment: string) => (ctx: FlowContext) => {
  const treatments = ctx.vars.tratamentos_sono
  return Array.isArray(treatments) && treatments.includes(treatment)
}

const hasClinicalFactor = (factor: string) => (ctx: FlowContext) => {
  const factors = ctx.vars.fatores_clinicos_sono
  return Array.isArray(factors) && factors.includes(factor)
}

/** Objective-specific steps: Sleep */
export const sleepSteps: Step[] = [
  // ========== INTRO ==========
  {
    id: 'intro_sleep',
    type: 'message',
    content:
      'Vamos entender como está seu sono, sua rotina e seu histórico para montar uma anamnese mais segura e um protocolo manipulado personalizado.',
  },

  // ========== MAIN COMPLAINTS ==========
  {
    id: 'sleep_concerns',
    type: 'choice',
    inputKey: 'preocupacoes_sono',
    multiple: true,
    minSelections: 1,
    content: 'O que mais está te incomodando hoje em relação ao sono?',
    options: [
      { label: 'Demoro para pegar no sono', value: 'dificuldade_iniciar' },
      { label: 'Acordo no meio da noite várias vezes', value: 'despertares_noturnos' },
      { label: 'Acordo muito cedo e não consigo voltar a dormir', value: 'despertar_precoce' },
      { label: 'Meu sono é leve / superficial', value: 'sono_leve' },
      { label: 'Acordo cansado(a), sem sensação de descanso', value: 'sono_nao_reparador' },
      { label: 'Ronco ou suspeita de apneia', value: 'ronco_apneia' },
      { label: 'Sonolência ou cansaço durante o dia', value: 'sonolencia_diurna' },
      { label: 'Ansiedade ou mente acelerada à noite', value: 'ansiedade_noturna' },
    ],
  },
  {
    id: 'sleep_onset_latency',
    type: 'choice',
    inputKey: 'latencia_sono',
    content: 'Quando vai dormir, quanto tempo costuma levar para pegar no sono?',
    condition: hasConcern('dificuldade_iniciar'),
    options: [
      { label: 'Até 20 minutos', value: 'ate_20' },
      { label: '20 a 40 minutos', value: '20_40' },
      { label: '40 a 60 minutos', value: '40_60' },
      { label: 'Mais de 60 minutos', value: 'mais_60' },
    ],
  },
  {
    id: 'sleep_night_awakenings',
    type: 'choice',
    inputKey: 'frequencia_despertares_sono',
    content: 'Com que frequência você acorda durante a madrugada ou antes do horário desejado?',
    condition: hasAnyConcern('despertares_noturnos', 'despertar_precoce'),
    options: [
      { label: '1 a 2 vezes por semana', value: '1_2_semana' },
      { label: '3 a 4 vezes por semana', value: '3_4_semana' },
      { label: '5 ou mais vezes por semana', value: '5_mais_semana' },
      { label: 'Quase todas as noites', value: 'quase_todas_noites' },
    ],
  },
  {
    id: 'sleep_anxiety_pattern',
    type: 'choice',
    inputKey: 'padrao_ansiedade_noturna',
    content: 'Como essa mente acelerada costuma aparecer à noite?',
    condition: hasConcern('ansiedade_noturna'),
    options: [
      { label: 'Penso demais antes de dormir', value: 'pensamentos_antes_dormir' },
      { label: 'Acordo no meio da noite com pensamentos acelerados', value: 'acordo_pensando' },
      { label: 'Sinto inquietação física ou tensão no corpo', value: 'inquietacao_tensao' },
      { label: 'Os três acontecem em momentos diferentes', value: 'combinado' },
    ],
  },
  {
    id: 'sleep_daytime_sleepiness',
    type: 'choice',
    inputKey: 'intensidade_sonolencia_diurna',
    content: 'Como está a sonolência ou cansaço ao longo do dia?',
    condition: hasConcern('sonolencia_diurna'),
    options: [
      { label: 'Leve, mas consigo funcionar bem', value: 'leve' },
      { label: 'Moderada, atrapalha produtividade ou humor', value: 'moderada' },
      { label: 'Intensa, tenho muito sono ou queda de energia', value: 'intensa' },
      { label: 'Oscila bastante conforme a noite anterior', value: 'oscilante' },
    ],
  },
  {
    id: 'sleep_routine_impact',
    type: 'choice',
    inputKey: 'impacto_rotina_sono',
    content: 'Quanto esse problema de sono impacta sua rotina hoje?',
    options: [
      { label: 'Pouco', value: 'pouco' },
      { label: 'Moderadamente', value: 'moderado' },
      { label: 'Bastante', value: 'alto' },
      { label: 'Muito, afeta humor, foco ou disposição', value: 'muito_alto' },
    ],
  },
  {
    id: 'sleep_hours',
    type: 'choice',
    inputKey: 'horas_sono',
    content: 'Quantas horas você dorme por noite, em média?',
    options: [
      { label: 'Menos de 5 horas', value: 'menos_5' },
      { label: '5 a 6 horas', value: '5_6' },
      { label: '7 a 8 horas', value: '7_8' },
      { label: 'Mais de 8 horas', value: 'mais_8' },
    ],
  },
  { ...sharedSleepQualityStep },

  // ========== HISTORY ==========
  {
    id: 'intro_sleep_history',
    type: 'message',
    content: 'Agora vamos falar do histórico do seu sono e do que já pode ter influenciado esse quadro.',
  },
  {
    id: 'sleep_duration_problem',
    type: 'choice',
    inputKey: 'duracao_queixa_sono',
    content: 'Há quanto tempo você percebe esse problema de sono?',
    options: [
      { label: 'Menos de 1 mês', value: 'menos_1_mes' },
      { label: '1 a 3 meses', value: '1_3_meses' },
      { label: '3 a 12 meses', value: '3_12_meses' },
      { label: 'Mais de 1 ano', value: 'mais_1_ano' },
    ],
  },
  {
    id: 'sleep_bad_nights_frequency',
    type: 'choice',
    inputKey: 'frequencia_noites_ruins',
    content: 'Em quantas noites por semana o sono costuma ficar ruim?',
    options: [
      { label: '1 a 2 noites', value: '1_2_noites' },
      { label: '3 a 4 noites', value: '3_4_noites' },
      { label: '5 a 6 noites', value: '5_6_noites' },
      { label: 'Todas ou quase todas', value: 'quase_todas' },
    ],
  },
  {
    id: 'sleep_triggers',
    type: 'choice',
    inputKey: 'gatilhos_sono',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Não percebo gatilho específico',
    content: 'Você percebe algum gatilho ou fator que piora seu sono?',
    options: [
      { label: 'Estresse ou ansiedade', value: 'estresse_ansiedade' },
      { label: 'Excesso de tela ou trabalho mental à noite', value: 'telas_trabalho' },
      { label: 'Horários irregulares para dormir', value: 'horarios_irregulares' },
      { label: 'Trabalho em turnos, plantão ou viagens', value: 'turnos_viagens' },
      { label: 'Dor, desconforto físico ou tensão muscular', value: 'dor_desconforto' },
      { label: 'Cafeína no fim do dia', value: 'cafeina' },
      { label: 'Álcool próximo ao horário de dormir', value: 'alcool' },
    ],
  },
  {
    id: 'sleep_current_treatments',
    type: 'choice',
    inputKey: 'tratamentos_sono',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Ainda não faço nada específico',
    content: 'O que você já faz hoje para tentar dormir melhor?',
    options: [
      { label: 'Higiene do sono / rotina para dormir', value: 'higiene_sono' },
      { label: 'Melatonina', value: 'melatonina' },
      { label: 'Magnésio, fitoterápicos ou chás calmantes', value: 'suplementos_fitoterapicos' },
      { label: 'Terapia ou acompanhamento psicológico', value: 'terapia' },
      { label: 'Medicamentos prescritos para dormir', value: 'medicacao_prescrita' },
      { label: 'CPAP ou outro dispositivo', value: 'cpap_dispositivo' },
      { label: 'Outros recursos', value: 'outros' },
    ],
  },
  {
    id: 'sleep_prescribed_meds',
    type: 'input',
    inputKey: 'tratamentos_sono_medicacoes',
    content: 'Quais medicamentos ou substâncias você usa para dormir?',
    condition: hasTreatment('medicacao_prescrita'),
    validate: validateTextoLivre,
  },
  {
    id: 'sleep_treatment_results',
    type: 'choice',
    inputKey: 'resultado_tratamentos_sono',
    content: 'Como você avalia o resultado do que já tentou até agora?',
    condition: hasCurrentTreatment,
    options: [
      { label: 'Ajuda bastante', value: 'ajuda_bastante' },
      { label: 'Ajuda um pouco', value: 'ajuda_pouco' },
      { label: 'Quase não faz diferença', value: 'quase_sem_diferenca' },
      { label: 'Perdeu efeito com o tempo', value: 'perdeu_efeito' },
    ],
  },
  { 
    id: 'sleep_clinical_factors',
    type: 'choice',
    inputKey: 'fatores_clinicos_sono',
    multiple: true,
    minSelections: 1,
    allowSkip: true,
    skipLabel: 'Nenhuma dessas',
    content: 'Alguma dessas situações também pode estar atrapalhando seu sono?',
    options: [
      { label: 'Ansiedade, irritabilidade ou humor deprimido', value: 'saude_mental' },
      { label: 'Rinite, nariz entupido ou dificuldade para respirar pelo nariz', value: 'obstrucao_nasal' },
      { label: 'Refluxo, azia ou desconforto digestivo à noite', value: 'refluxo' },
      { label: 'Dor crônica ou tensão muscular', value: 'dor_cronica' },
      { label: 'Acordo para urinar durante a madrugada', value: 'nocturia' },
      { label: 'Calor, suor noturno ou fogachos', value: 'calor_noturno' },
      { label: 'Outro fator importante', value: 'outros' },
    ],
  },
  {
    id: 'sleep_clinical_factors_other',
    type: 'input',
    inputKey: 'fatores_clinicos_sono_outros',
    content: 'Qual outro fator você considera importante para o seu sono?',
    condition: hasClinicalFactor('outros'),
    validate: validateTextoLivre,
  },

  // ========== LIFESTYLE ==========
  {
    id: 'intro_sleep_lifestyle',
    type: 'message',
    content: 'Por fim, vamos entender sua rotina, porque ela costuma influenciar muito a qualidade do sono.',
  },
  {
    id: 'sleep_caffeine',
    type: 'choice',
    inputKey: 'cafeina_tarde_noite',
    content: 'Você costuma consumir café, energético ou pré-treino após as 16h?',
    options: [
      { label: 'Não', value: 'nao' },
      { label: 'Às vezes', value: 'as_vezes' },
      { label: 'Quase todos os dias', value: 'quase_todos_dias' },
      { label: 'Todos os dias', value: 'todos_dias' },
    ],
  },
  {
    id: 'sleep_alcohol',
    type: 'choice',
    inputKey: 'alcool_proximo_sono',
    content: 'Você costuma beber álcool próximo ao horário de dormir?',
    options: [
      { label: 'Não', value: 'nao' },
      { label: 'Raramente', value: 'raramente' },
      { label: '1 a 2 vezes por semana', value: '1_2_semana' },
      { label: '3 ou mais vezes por semana', value: '3_mais_semana' },
    ],
  },
  {
    id: 'sleep_screens',
    type: 'choice',
    inputKey: 'uso_telas_antes_dormir',
    content: 'Na última hora antes de dormir, como costuma ser seu uso de telas?',
    options: [
      { label: 'Quase não uso', value: 'quase_nao_uso' },
      { label: 'Uso pouco tempo', value: 'uso_pouco' },
      { label: 'Uso bastante, mas tento reduzir', value: 'uso_bastante' },
      { label: 'Uso intensamente até dormir', value: 'uso_intenso' },
    ],
  },
  {
    id: 'sleep_bedtime_consistency',
    type: 'choice',
    inputKey: 'regularidade_horario_sono',
    content: 'Seus horários para dormir e acordar costumam ser regulares?',
    options: [
      { label: 'Sim, quase sempre', value: 'regular' },
      { label: 'Oscilam um pouco', value: 'oscilam_pouco' },
      { label: 'São bem irregulares', value: 'irregular' },
      { label: 'Mudam muito por trabalho ou rotina', value: 'muito_irregular' },
    ],
  },
  {
    id: 'sleep_shift_work',
    type: 'choice',
    inputKey: 'rotina_turnos_viagens',
    content: 'Sua rotina costuma atrapalhar o ciclo do sono?',
    options: [
      { label: 'Não', value: 'nao' },
      { label: 'Às vezes, por viagens ou eventos', value: 'as_vezes' },
      { label: 'Sim, por trabalho em turnos ou plantões', value: 'turnos' },
      { label: 'Sim, por rotina familiar / filhos / demandas noturnas', value: 'rotina_noturna' },
    ],
  },
  { ...sharedStressLevelStep },
  { ...sharedActivityFrequencyStep },
]
